package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.intelligence.ConciergeHandoff
import com.steamstreet.vegasful.browser.appScope
import com.steamstreet.vegasful.browser.components.NeonSky
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.w3c.dom.Element
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import kotlin.math.max

/**
 * The home page hero (`home-hero`): the neon headline, the city lights behind it, and the switch
 * that turns the search box into the concierge.
 *
 * The server renders a complete, working hero; this module only adds to it. The searching itself
 * stays with `global-search`, which reads the `data-mode` this module keeps on
 * the box. See `specs/web-home-hero.md`.
 */
fun homeHero(element: Element) {
    // A visitor who asked the concierge before signing in lands back here after signing in,
    // because that is where sign-in returns. Their question is waiting, so take them to it.
    if (Account.isLoggedIn() && ConciergeHandoff.isHeld()) {
        window.location.href = ConciergeHandoff.CONCIERGE_PATH
        return
    }
    HomeHero(element as HTMLElement).start()
}

private class HomeHero(private val root: HTMLElement) {
    private val sky = (root.querySelector(".vf-sky") as? HTMLCanvasElement)?.let { NeonSky(root, it) }
    private val searchBox = root.querySelector("[data-module=global-search]") as? HTMLElement
    private val input = root.querySelector(".vf-input") as? HTMLInputElement
    private val bell = root.querySelector(".vf-bell") as? HTMLElement
    private val go = root.querySelector(".vf-go") as? HTMLElement
    private val neon = root.querySelector(".vf-neon") as? HTMLElement
    private val star = root.querySelector(".vf-star") as? HTMLElement
    private val ring = root.querySelector(".vf-ring")
    private val neonBox = root.querySelector(".vf-neonbox") as? HTMLElement

    private val reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    private val searchPlaceholder = input?.placeholder ?: ""

    private var words = listOf<HTMLElement>()
    private var widths = listOf<Double>()
    private var currentWord = 0
    private var flipping = false
    private val timers = mutableListOf<Int>()

    private var hintJob: Job? = null

    fun start() {
        buildWords()

        sizeRing()
        root.classList.add("vf-ready")
        sky?.onResize = {
            measureWords()
            sizeRing()
        }
        sky?.start()
        observeRing()

        bell?.addEventListener("click", {
            setMode(if (root.getAttribute("data-mode") == "ask") "search" else "ask")
            input?.focus()
            if (root.getAttribute("data-mode") == "ask") sky?.burst(bell)
        })
        neon?.addEventListener("click", { flip() })

        if (!reducedMotion) flip()
    }

    // ---------------------------------------------------------------- the concierge switch

    private fun setMode(mode: String) {
        root.setAttribute("data-mode", mode)
        searchBox?.setAttribute("data-mode", mode)
        val asking = mode == "ask"
        bell?.setAttribute("aria-pressed", asking.toString())
        input?.setAttribute("aria-label", if (asking) "Ask the concierge" else "Search Vegasful")
        go?.textContent = if (asking) "Ask →" else "Search"

        hintJob?.cancel()
        if (asking) {
            startHints()
        } else {
            input?.placeholder = searchPlaceholder
        }
    }

    /** The concierge's own example questions, typed into the placeholder one after another. */
    private fun startHints() {
        val field = input ?: return
        if (reducedMotion) {
            field.placeholder = ConciergeHandoff.EXAMPLE_QUESTIONS.first()
            return
        }
        hintJob = appScope.launch {
            while (isActive) {
                for (hint in ConciergeHandoff.EXAMPLE_QUESTIONS) {
                    // While the visitor is in the box the example holds still, whole, so it reads
                    // as a suggestion rather than something moving under their cursor.
                    fun interrupted() = document.activeElement == field || field.value.isNotBlank()
                    while (interrupted()) {
                        field.placeholder = if (field.value.isBlank()) hint else ""
                        delay(300)
                    }
                    for (i in 1..hint.length) {
                        if (interrupted()) break
                        field.placeholder = hint.substring(0, i)
                        delay(45)
                    }
                    if (interrupted()) continue
                    delay(1800)
                    for (i in field.placeholder.length downTo 0) {
                        if (interrupted()) break
                        field.placeholder = field.placeholder.substring(0, i)
                        delay(18)
                    }
                    delay(400)
                }
            }
        }
    }

    // ---------------------------------------------------------------- the neon tube

    private fun sizeRing() {
        val box = neonBox ?: return
        val svg = ring ?: return
        val rect = box.getBoundingClientRect()
        val w = rect.width + 12
        val h = rect.height + 12
        svg.setAttribute("viewBox", "0 0 $w $h")
        svg.querySelectorAll("rect").asList().forEach {
            (it as Element).apply {
                setAttribute("x", "2")
                setAttribute("y", "2")
                setAttribute("width", "${max(1.0, w - 4)}")
                setAttribute("height", "${max(1.0, h - 4)}")
                setAttribute("rx", "19")
            }
        }
    }

    // ---------------------------------------------------------------- the neon headline

    /**
     * Add the flip words in front of the headline's real last word. They are built here rather
     * than sent by the server so that the page, and anything reading it, only ever says the real
     * headline; they are hidden from assistive technology for the same reason.
     */
    private fun buildWords() {
        val container = neon ?: return
        val final = container.querySelector(".vf-word") as? HTMLElement ?: return
        val extra = container.getAttribute("data-words").orEmpty().split(",").map { it.trim() }.filter { it.isNotEmpty() }
        val built = extra.map { word ->
            (document.createElement("span") as HTMLElement).apply {
                className = "vf-word"
                setAttribute("data-c", word.lowercase())
                setAttribute("aria-hidden", "true")
                textContent = word
                container.insertBefore(this, final)
            }
        }
        words = built + final
        currentWord = words.size - 1
        measureWords()
        // The words are measured again once the web font has arrived, or the first widths would
        // be the fallback face's.
        val fonts = document.asDynamic().fonts
        if (fonts != null && fonts != undefined) {
            fonts.ready.then({ _: dynamic -> measureWords() }, { _: dynamic -> })
        }
    }

    private fun measureWords() {
        widths = words.map { it.getBoundingClientRect().width }
        widths.getOrNull(currentWord)?.takeIf { it > 0 }?.let { neon?.style?.width = "${it}px" }
    }

    private fun later(ms: Int, block: () -> Unit) {
        timers += window.setTimeout(block, ms)
    }

    private fun clearTimers() {
        timers.forEach { window.clearTimeout(it) }
        timers.clear()
    }

    /**
     * Cycle the neon word once and land on the real one. Each change is a sign switching: the old
     * word cuts out, the unlit glass of the next shows for a moment, then it buzzes on. The sky
     * and about half the lights take on each word's colour as it lights.
     */
    private fun flip() {
        if (reducedMotion || words.size < 2) return
        clearTimers()
        flipping = true
        root.classList.add("flipping")
        val last = words.size - 1
        var t = 250
        for (i in 0..last) {
            later(t) {
                val old = words[currentWord]
                if (currentWord != i) {
                    old.classList.remove("buzz")
                    old.classList.add("out")
                }
            }
            later(t + 140) {
                words.forEachIndexed { j, w ->
                    w.classList.remove("out", "buzz", "lit", "glass")
                    if (j == i) w.classList.add("glass")
                }
                currentWord = i
                widths.getOrNull(i)?.let { neon?.style?.width = "${it}px" }
                val name = words[i].getAttribute("data-c").orEmpty()
                if (i < last && sky?.hasSky(name) == true) {
                    sky.show(name)
                    sky.accent(ACCENTS[name])
                } else {
                    sky?.followHour()
                    sky?.accent(null)
                }
            }
            later(t + 360) {
                val w = words[i]
                w.classList.remove("glass")
                w.getBoundingClientRect()
                w.classList.add("lit", "buzz")
                if (i == last) land()
            }
            t += 1150
        }
    }

    private fun land() {
        flipping = false
        later(250) { root.classList.remove("flipping") }
        later(420) { star?.let { sky?.burst(it) } }
    }

    // ---------------------------------------------------------------- the ring's size

    /** Keep the neon tube fitted to the box as the box changes size. */
    private fun observeRing() {
        val box = neonBox ?: return
        val resizeObserver = window.asDynamic().ResizeObserver
        if (resizeObserver != undefined) {
            js("Reflect").construct(resizeObserver, arrayOf<dynamic>({ _: dynamic -> sizeRing() })).observe(box)
        }
    }
}

/** The colour each flip word lends the lights while it is lit. */
private val ACCENTS = mapOf(
    "night" to doubleArrayOf(57.0, 198.0, 255.0),
    "sunset" to doubleArrayOf(255.0, 138.0, 61.0),
    "dinner" to doubleArrayOf(255.0, 63.0, 108.0),
    "weekend" to doubleArrayOf(61.0, 255.0, 154.0)
)
